import React, { useState, useEffect } from "react";
import { saveMessageContactForm } from "./../../services/contactformServices";
import { getSettings } from "../../services/settingsServices";
import Button from "./../../components/Button/Button";
import { FaMailBulk, FaMapMarkerAlt, FaPhoneAlt } from "react-icons/fa";

// (optionnel) rend /uploads/... absolu vers l’API
const toAbsolute = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s;
  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

const ContactForm = () => {
  const [notificationSent, setNotificationSent] = useState(false);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  // Récupération des settings (infos comité)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        if (Array.isArray(data) && data.length > 0) {
          setSettings(data[0]);
        }
      } catch (err) {
        console.error("Erreur lors de la récupération des paramètres", err);
      }
    };
    fetchSettings();
  }, []);

  // Auto-hide du message de succès
  useEffect(() => {
    if (!notificationSent) return;
    const t = setTimeout(() => setNotificationSent(false), 5000);
    return () => clearTimeout(t);
  }, [notificationSent]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setError("");
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const { name, email, message } = formData;

    if (!name || !email || !message) {
      setError("Merci de remplir tous les champs avant d'envoyer votre message.");
      return;
    }

    try {
      await saveMessageContactForm({ name, email, message });
      setNotificationSent(true);
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("Erreur lors de la sauvegarde du message", err);
      setError(
        "Une erreur est survenue lors de l'envoi. Vous pouvez réessayer ou nous contacter directement par e-mail."
      );
    }
  };

  const nomComite = settings?.nom_comite || "Comité Départemental de Judo";
  const adresseComite = settings?.adresse_comite || "Adresse du comité";
  const telComite = settings?.tel_comite || "";
  const mailComite = settings?.mail_comite || "";

  return (
    <section className="py-12 md:py-16 bg-gradient-to-b from-slate-50 via-slate-100 to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center mb-8 md:mb-10">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-2">
            Contact
          </p>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Contactez le comité
          </h2>
          <p className="mt-3 text-sm md:text-base text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Une question, une suggestion, un projet avec votre club&nbsp;? 
            Écrivez-nous et l’équipe du comité vous répondra dans les meilleurs délais.
          </p>
        </div>

        {/* Carte principale */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 items-stretch">
          {/* Colonne gauche : infos de contact (données depuis settings) */}
          <div className="md:col-span-2">
            <div className="relative h-full rounded-2xl bg-gradient-to-br from-primary via-primary to-indigo-600 text-white shadow-xl overflow-hidden">
              <div className="absolute -top-16 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-16 -left-10 w-40 h-40 bg-black/10 rounded-full blur-3xl" />

              <div className="relative z-10 p-6 md:p-7 flex flex-col h-full justify-between">
                <div>
                  {/* Logo comité si présent */}
                  {settings?.logo_comite && (
                    <img
                      src={toAbsolute(settings.logo_comite)}
                      alt={nomComite}
                      className="h-20 md:h-20 w-auto mb-4 rounded-md bg-white/90 p-1 shadow-md"
                    />
                  )}

                  <h3 className="text-lg md:text-xl font-bold mb-1">
                    {nomComite}
                  </h3>
                  <p className="text-xs md:text-sm text-white/80 mb-4">
                    Le comité est à l’écoute des clubs, enseignants, arbitres et
                    judokas de tout le département.
                  </p>

                  <div className="space-y-3 text-xs md:text-sm">
                    {/* Adresse */}
                    {adresseComite && (
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <FaMapMarkerAlt className="w-4 h-4" />
                        </div>
                        <p>{adresseComite}</p>
                      </div>
                    )}

                    {/* Téléphone */}
                    {telComite && (
                      <div className="flex items-center gap-3">
                        <FaPhoneAlt className="w-4 h-4" />
                        <a
                          href={`tel:${telComite}`}
                          className="hover:underline"
                        >
                          {telComite}
                        </a>
                      </div>
                    )}

                    {/* E-mail */}
                    {mailComite && (
                      <div className="flex items-center gap-3">
                        <FaMailBulk className="w-4 h-4" />
                        <a
                          href={`mailto:${mailComite}`}
                          className="hover:underline"
                        >
                          {mailComite}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-white/15 text-[11px] text-white/80">
                  <p>
                    Vos informations sont utilisées uniquement pour traiter votre
                    message et ne sont jamais partagées à des tiers.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne droite : formulaire */}
          <div className="md:col-span-3">
            <div className="relative bg-white/90 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl shadow-lg overflow-hidden">
              {/* Bandeau haut animé */}
              <div className="h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400 animate-[pulse_4s_ease-in-out_infinite]" />

              <div className="p-5 md:p-6">
                {/* Message succès / erreur */}
                {notificationSent && (
                  <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs md:text-sm px-3 py-2 flex items-start gap-2">
                    <span className="mt-[2px] text-base">✅</span>
                    <p>
                      Votre message a bien été envoyé. Merci pour votre prise de contact,
                      le comité vous répondra dès que possible.
                    </p>
                  </div>
                )}

                {error && (
                  <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-xs md:text-sm px-3 py-2 flex items-start gap-2">
                    <span className="mt-[2px] text-base">⚠️</span>
                    <p>{error}</p>
                  </div>
                )}

                <form className="space-y-5" onSubmit={handleSubmit}>
                  {/* Email */}
                  <div className="space-y-1.5">
                    <label
                      htmlFor="email"
                      className="block text-xs font-semibold tracking-wide uppercase text-slate-700 dark:text-slate-200"
                    >
                      Votre e-mail
                    </label>
                    <div className="relative group">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full rounded-xl border border-slate-300/80 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-800/80 text-sm text-slate-900 dark:text-white px-3.5 py-2.5 outline-none transition-all duration-150 focus:ring-2 focus:ring-primary/60 focus:border-primary/70 group-hover:border-primary/40"
                        placeholder="vous@exemple.fr"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Nom prénom */}
                  <div className="space-y-1.5">
                    <label
                      htmlFor="name"
                      className="block text-xs font-semibold tracking-wide uppercase text-slate-700 dark:text-slate-200"
                    >
                      Nom & prénom
                    </label>
                    <div className="relative group">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="w-full rounded-xl border border-slate-300/80 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-800/80 text-sm text-slate-900 dark:text-white px-3.5 py-2.5 outline-none transition-all duration-150 focus:ring-2 focus:ring-primary/60 focus:border-primary/70 group-hover:border-primary/40"
                        placeholder="Votre nom et prénom"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5">
                    <label
                      htmlFor="message"
                      className="block text-xs font-semibold tracking-wide uppercase text-slate-700 dark:text-slate-200"
                    >
                      Votre message
                    </label>
                    <div className="relative group">
                      <textarea
                        id="message"
                        name="message"
                        rows="5"
                        className="w-full rounded-xl border border-slate-300/80 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-800/80 text-sm text-slate-900 dark:text-white px-3.5 py-2.5 outline-none transition-all duration-150 focus:ring-2 focus:ring-primary/60 focus:border-primary/70 group-hover:border-primary/40 resize-y min-h-[140px]"
                        placeholder="Expliquez-nous votre demande, votre projet ou votre besoin..."
                        required
                        value={formData.message}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Bouton */}
                  <div className="pt-2 flex items-center justify-center">
                    <Button
                      text="Envoyer mon message"
                      icon={FaMailBulk}
                      hoverLabel="Envoyer le message au comité"
                      type="submit"
                      className="gap-2 px-6 py-2"
                    />
                  </div>

                  <p className="mt-1 text-[11px] text-center text-slate-500 dark:text-slate-400">
                    En envoyant ce formulaire, vous acceptez que le comité vous contacte
                    par e-mail à propos de votre demande.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
